import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import axios from 'axios';

const textVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.8,
            ease: 'easeOut',
        },
    },
};

const Content1 = ({ isAdmin = false }) => {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentContent, setCurrentContent] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch all content from API
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await axios.get('https://madhusewingmachines.com/api/content2');
                if (response.data) {
                    setContents([response.data]); // Wrap in array since your backend returns single content
                }
            } catch (err) {
                setError('Failed to fetch content');
                console.error('Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [success]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleEditClick = (content) => {
        setCurrentContent(content);
        setFormData({
            title: content.title,
            description: content.description
        });
        if (content.image) {
            setImagePreview(`https://madhusewingmachines.com/uploads/${content.image}`);
        }
        setShowEditModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            let response;
            if (currentContent) {
                // Update existing content
                response = await axios.put(
                    'https://madhusewingmachines.com/api/content2',
                    formDataToSend,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            } else {
                // Create new content
                response = await axios.post(
                    'https://madhusewingmachines.com/api/content2',
                    formDataToSend,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            }

            setSuccess(currentContent ? 'Content updated successfully!' : 'Content created successfully!');
            setShowEditModal(false);
            setCurrentContent(null);
            setFormData({
                title: '',
                description: '',
                image: null
            });
            setImagePreview('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save content');
            console.error('Submit error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                await axios.delete(`https://madhusewingmachines.com/api/content2/${id}`);
                setSuccess('Content deleted successfully!');
                setContents([]); // Clear contents since your backend only handles single content
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete content');
                console.error('Delete error:', err);
            }
        }
    };

    if (loading) return (
        <Container fluid className="py-5 text-center">
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        </Container>
    );

    if (error) return (
        <Container fluid className="py-5 text-center text-danger">
            <Alert variant="danger">{error}</Alert>
        </Container>
    );

    return (
        <Container fluid className="py-5 px-3 px-md-5 bg-light">
            {/* Admin Controls */}
            {isAdmin && (
                <div className="text-end mb-3">
                    <Button
                        variant="primary"
                        onClick={() => {
                            setCurrentContent(null);
                            setFormData({
                                title: '',
                                description: '',
                                image: null
                            });
                            setImagePreview('');
                            setShowEditModal(true);
                        }}
                        className="me-2"
                    >
                        {contents.length > 0 ? 'Update Banner' : 'Add Banner'}
                    </Button>
                    {contents.length > 0 && (
                        <Button
                            variant="danger"
                            onClick={() => handleDelete(contents[0]._id)}
                        >
                            Delete Banner
                        </Button>
                    )}
                </div>
            )}

            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {/* Display content */}
            {contents.length > 0 ? (
                <Row className="align-items-center flex-column-reverse flex-md-row mb-5">
                    {/* Left Image */}
                    <Col md={6} className="mb-4 mb-md-0 p-0 order-md-1">
                        <motion.img
                            src={`https://madhusewingmachines.com/uploads/${contents[0].image}`}
                            alt="Showcase"
                            className="img-fluid rounded-4 shadow-lg w-100"
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                        />
                    </Col>

                    {/* Right Content */}
                    <Col md={6} className="order-md-2">
                        <motion.div
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-md-start text-center"
                        >
                            <h1 className="display-5 fw-bold mb-4 text-black">
                                {contents[0].title}
                            </h1>
                            <p className="lead mb-4 text-secondary">
                                {contents[0].description}
                            </p>
                        </motion.div>
                    </Col>
                </Row>
            ) : (
                <Row className="align-items-center flex-column-reverse flex-md-row">
                    <Col md={6} className="mb-4 mb-md-0 p-0">
                        <motion.div
                            className="img-fluid rounded-4 shadow-lg w-100 bg-secondary"
                            style={{ height: '400px' }}
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                        />
                    </Col>
                    <Col md={6}>
                        <motion.div
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-md-start text-center"
                        >
                            <h1 className="display-5 fw-bold mb-4 text-black">
                                No Banner Available
                            </h1>
                            <p className="lead mb-4 text-secondary">
                                {isAdmin ? 'Add a banner to display here' : 'Check back later for updates'}
                            </p>
                        </motion.div>
                    </Col>
                </Row>
            )}

            {/* Edit/Add Modal for Admin */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{currentContent ? 'Update Banner' : 'Add Banner'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Title*</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                {currentContent ? 'Update Image' : 'Upload Image*'}
                            </Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                required={!currentContent}
                            />
                            {imagePreview && (
                                <div className="mt-3">
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                                    />
                                </div>
                            )}
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowEditModal(false);
                                    setCurrentContent(null);
                                }}
                                className="me-2"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        <span className="ms-2">Saving...</span>
                                    </>
                                ) : currentContent ? 'Update' : 'Save'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default Content1;